import React, { useState, useEffect } from 'react';
import { useEvent } from '@soap/modules';

export interface EducationSystemOption {
  key: string;
  displayName: string;
  isBuiltIn: boolean;
}

export function pickDefaultEducationKey(mapped: EducationSystemOption[]): string {
  if (mapped.length === 0) return '';
  return mapped.find((m) => m.isBuiltIn)?.key ?? mapped[0]!.key;
}

export interface CustomSubjectFormProps {
  active: boolean;
  onLoadEducationSystems?: () => void;
  onNavigateToEducationSystem?: () => void;
  subjectName: string;
  setSubjectName: (value: string) => void;
  educationSystems: EducationSystemOption[];
  educationSystemKey: string;
  setEducationSystemKey: (value: string) => void;
}

export function useCustomSubjectFormState(active: boolean, onLoadEducationSystems?: () => void) {
  const [subjectName, setSubjectName] = useState('');
  const [educationSystems, setEducationSystems] = useState<EducationSystemOption[]>([]);
  const [educationSystemKey, setEducationSystemKey] = useState('');

  useEvent({
    eventName: 'E311v1_GotEducationSystems',
    onEventReceived: (event: any) => {
      const raw = event?.e311_Systems ?? event?.E311_Systems;
      if (!Array.isArray(raw)) return;
      const mapped: EducationSystemOption[] = raw
        .map((row: any) => ({
          key: String(row?.e311_Key ?? row?.E311_Key ?? ''),
          displayName: String(row?.e311_DisplayName ?? row?.E311_DisplayName ?? ''),
          isBuiltIn: Boolean(row?.e311_IsBuiltIn ?? row?.E311_IsBuiltIn)
        }))
        .filter((o: EducationSystemOption) => o.key && o.displayName);
      setEducationSystems(mapped);
      setEducationSystemKey((prev) =>
        mapped.some((m) => m.key === prev) ? prev : pickDefaultEducationKey(mapped)
      );
    }
  });

  useEvent({
    eventName: 'E312v1_TenantEducationSystemAdded',
    onEventReceived: (event: any) => {
      const key = event?.e312_Key ?? event?.E312_Key;
      const displayName = event?.e312_DisplayName ?? event?.E312_DisplayName;
      if (!key || !displayName) return;
      setEducationSystems((prev) => {
        if (prev.some((p) => p.key === key)) return prev;
        return [...prev, { key: String(key), displayName: String(displayName), isBuiltIn: false }].sort(
          (a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
        );
      });
      setEducationSystemKey(String(key));
    }
  });

  useEffect(() => {
    if (!active) return;
    onLoadEducationSystems?.();
  }, [active, onLoadEducationSystems]);

  useEffect(() => {
    if (!active) {
      setSubjectName('');
      setEducationSystems([]);
      setEducationSystemKey('');
    }
  }, [active]);

  const canSave = subjectName.trim().length > 0 && educationSystemKey.trim().length > 0;

  const getEnsureSubjectCommand = () => ({
    c316_SubjectName: subjectName.trim(),
    c316_EducationSystemKey: educationSystemKey.trim()
  });

  return {
    subjectName,
    setSubjectName,
    educationSystems,
    educationSystemKey,
    setEducationSystemKey,
    canSave,
    getEnsureSubjectCommand,
    reset: () => {
      setSubjectName('');
      setEducationSystems([]);
      setEducationSystemKey('');
    }
  };
}

export function CustomSubjectForm({
  active,
  onLoadEducationSystems,
  onNavigateToEducationSystem,
  subjectName,
  setSubjectName,
  educationSystems,
  educationSystemKey,
  setEducationSystemKey
}: CustomSubjectFormProps) {
  useEffect(() => {
    if (!active) return;
    onLoadEducationSystems?.();
  }, [active, onLoadEducationSystems]);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Subject name <span className="text-destructive-action">*</span>
        </label>
        <p className="mb-2 text-xs text-gray-600">
          Using the same name as a subject from a built‑in curriculum (e.g. English) is fine.
        </p>
        <input
          type="text"
          value={subjectName}
          onChange={(e) => setSubjectName(e.target.value)}
          placeholder="e.g. Karate"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
        />
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Education system <span className="text-destructive-action">*</span>
        </label>
        <p className="mb-2 text-xs text-gray-500">
          Education systems define standards, terminology, and Key Stage / Year labels for this subject.
        </p>
        <select
          value={educationSystemKey}
          onChange={(e) => setEducationSystemKey(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
        >
          {educationSystems.length === 0 ? (
            <option value="" disabled>
              Loading education systems…
            </option>
          ) : (
            educationSystems.map((sys) => (
              <option key={sys.key} value={sys.key}>
                {sys.displayName}
                {sys.isBuiltIn ? '' : ' (custom)'}
              </option>
            ))
          )}
        </select>
        {onNavigateToEducationSystem && (
          <button
            type="button"
            onClick={onNavigateToEducationSystem}
            className="mt-2 text-sm text-action-blue underline hover:text-action-blue-dark"
          >
            Add new education system
          </button>
        )}
      </div>
    </div>
  );
}
